"use client";

import { useEffect, useState, useRef, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import RealProductCard from "@/components/RealProductCard";
import type { Product } from "@/types";

/**
 * 추천 carousel 아이템의 단일 진실 너비 클래스.
 * gap(12px = gap-3)을 정확히 반영해 한 화면에 2/4/5/6/7열을 노출한다.
 * carousel 아이템과 skeleton이 반드시 동일 값을 써야 로딩→완료 시 레이아웃 시프트가 없다.
 */
const CARD_WIDTH_CLASS =
  "shrink-0 w-[calc((100%-24px)/2)] sm:w-[calc((100%-36px)/4)] md:w-[calc((100%-48px)/5)] lg:w-[calc((100%-60px)/6)] 3xl:w-[calc((100%-72px)/7)]";

interface ProductCarouselProps {
  products: Product[];
  isLoading: boolean;
  title: string;
  description?: string;
  /** 헤더 우측 영역 (예: Partner의 "모두 보기" 링크). 네비 버튼 좌측에 렌더된다. */
  headerAction?: ReactNode;
  /** 레이아웃 모드: horizontal(가로 스크롤) 또는 vertical(세로 그리드) */
  layout?: "horizontal" | "vertical";
}

export default function ProductCarousel({
  products,
  isLoading,
  title,
  description,
  headerAction,
  layout = "horizontal",
}: ProductCarouselProps) {
  const isVertical = layout === "vertical";
  const scrollRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  // 스크롤 상태 업데이트
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const update = () => {
      setAtStart(el.scrollLeft <= 1);
      setAtEnd(el.scrollLeft >= el.scrollWidth - el.clientWidth - 1);
    };

    update();
    el.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [products]);

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el || !el.firstElementChild) return;

    const cardWidth = (el.firstElementChild as HTMLElement).offsetWidth + 12;
    const amount = direction === "left" ? -cardWidth : cardWidth;
    el.scrollBy({ left: amount, behavior: "smooth" });
  };

  // 로딩이 끝났는데 결과가 없으면 섹션 전체 비표시
  if (!isLoading && products.length === 0) {
    return null;
  }

  return (
    <section className="pb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold">{title}</h2>
          {description && (
            <p className="text-sm text-gray-500 mt-1">{description}</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {headerAction}
          {/* 데스크탑에서만 좌우 버튼 표시 (horizontal 모드일 때만) */}
          {!isVertical && !isLoading && (
            <div className="hidden md:flex gap-2">
              <button
                onClick={() => scroll("left")}
                disabled={atStart}
                className="p-2 border border-gray-300 rounded-full transition disabled:opacity-30 disabled:cursor-not-allowed hover:enabled:shadow-md"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => scroll("right")}
                disabled={atEnd}
                className="p-2 border border-gray-300 rounded-full transition disabled:opacity-30 disabled:cursor-not-allowed hover:enabled:shadow-md"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 로딩 중: 제목/설명은 위에서 이미 실제로 렌더, 상품 영역만 skeleton */}
      {isLoading ? (
        <div
          className={
            isVertical
              ? "grid grid-cols-2 gap-3"
              : "flex gap-3 overflow-hidden -mx-4 px-4 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0"
          }
        >
          {[...Array(isVertical ? 4 : 6)].map((_, i) => (
            <div
              key={i}
              className={isVertical ? "animate-pulse" : `animate-pulse ${CARD_WIDTH_CLASS}`}
            >
              <div className="aspect-square bg-gray-200 rounded-3xl" />
              <div className="mt-2 h-4 w-3/4 bg-gray-200 rounded" />
              <div className="mt-1 h-4 w-1/2 bg-gray-100 rounded" />
            </div>
          ))}
        </div>
      ) : isVertical ? (
        <div className="grid grid-cols-2 gap-3">
          {products.map((product) => (
            <div key={product.id}>
              <RealProductCard product={product} />
            </div>
          ))}
        </div>
      ) : (
        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto scrollbar-hide pb-2 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {products.map((product) => (
            <div key={product.id} className={CARD_WIDTH_CLASS}>
              <RealProductCard product={product} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
